import { useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  CheckCircle2, XCircle, Loader2, Search, Building2, MapPin, ShieldCheck, AlertTriangle,
} from "lucide-react";

// ========================
// TYPES
// ========================

export interface GSTINDetails {
  gstin: string;
  tradeName: string;
  legalName: string;
  address: string;
  city: string;
  state: string;
  stateCode: string;
  pincode: string;
  status: "Active" | "Inactive" | "Cancelled" | "Suspended";
  registrationType: string;
  registrationDate: string;
  lastFiledReturn: string;
  isValid: boolean;
}

type ValidationStatus = "idle" | "validating" | "valid" | "invalid" | "error";

// ========================
// MOCK GSTIN DATABASE
// ========================

const MOCK_GSTIN_DB: Record<string, GSTINDetails> = {
  "27AABCU9603R1ZM": {
    gstin: "27AABCU9603R1ZM", tradeName: "ABC Trading Company", legalName: "ABC Trading Company Private Limited",
    address: "Office 401, Trade Tower, Andheri East", city: "Mumbai", state: "Maharashtra", stateCode: "27",
    pincode: "400069", status: "Active", registrationType: "Regular", registrationDate: "2020-07-15", lastFiledReturn: "Feb 2026", isValid: true,
  },
  "07AABCA1234E1ZQ": {
    gstin: "07AABCA1234E1ZQ", tradeName: "Acme Corporation", legalName: "Acme Corporation Private Limited",
    address: "42, Connaught Place", city: "New Delhi", state: "Delhi", stateCode: "07",
    pincode: "110001", status: "Active", registrationType: "Regular", registrationDate: "2018-01-10", lastFiledReturn: "Feb 2026", isValid: true,
  },
  "29AABCB5678F1ZR": {
    gstin: "29AABCB5678F1ZR", tradeName: "Beta Industries", legalName: "Beta Industries Private Limited",
    address: "Plot 15, KIADB Industrial Area, Whitefield", city: "Bangalore", state: "Karnataka", stateCode: "29",
    pincode: "560066", status: "Active", registrationType: "Regular", registrationDate: "2019-04-20", lastFiledReturn: "Jan 2026", isValid: true,
  },
  "33AABCE7890I1ZX": {
    gstin: "33AABCE7890I1ZX", tradeName: "Epsilon Technologies", legalName: "Epsilon Technologies LLP",
    address: "Block C, IT Park, Sholinganallur", city: "Chennai", state: "Tamil Nadu", stateCode: "33",
    pincode: "600119", status: "Active", registrationType: "Regular", registrationDate: "2021-09-01", lastFiledReturn: "Feb 2026", isValid: true,
  },
  "06AABCG3456H1ZT": {
    gstin: "06AABCG3456H1ZT", tradeName: "Gamma Traders", legalName: "Gamma Traders & Co",
    address: "SCO 45, Sector 17", city: "Chandigarh", state: "Haryana", stateCode: "06",
    pincode: "160017", status: "Inactive", registrationType: "Regular", registrationDate: "2017-11-01", lastFiledReturn: "Sep 2025", isValid: false,
  },
  "27AABCD9012G1ZS": {
    gstin: "27AABCD9012G1ZS", tradeName: "Delta Services", legalName: "Delta Services India Pvt Ltd",
    address: "A-Wing, Business Hub, Powai", city: "Mumbai", state: "Maharashtra", stateCode: "27",
    pincode: "400076", status: "Active", registrationType: "Composition", registrationDate: "2020-02-14", lastFiledReturn: "Feb 2026", isValid: true,
  },
};

// GSTIN format: 2-digit state code + 10-char PAN + 1 entity code + 1 Z + 1 checksum
const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

// ========================
// HOOK
// ========================

const STATE_NAMES: Record<string, string> = {
  "01": "Jammu & Kashmir", "02": "Himachal Pradesh", "03": "Punjab", "04": "Chandigarh", "05": "Uttarakhand",
  "06": "Haryana", "07": "Delhi", "08": "Rajasthan", "09": "Uttar Pradesh", "10": "Bihar",
  "11": "Sikkim", "12": "Arunachal Pradesh", "13": "Nagaland", "14": "Manipur", "15": "Mizoram",
  "16": "Tripura", "17": "Meghalaya", "18": "Assam", "19": "West Bengal", "20": "Jharkhand",
  "21": "Odisha", "22": "Chhattisgarh", "23": "Madhya Pradesh", "24": "Gujarat", "26": "Dadra & Nagar Haveli",
  "27": "Maharashtra", "29": "Karnataka", "30": "Goa", "32": "Kerala", "33": "Tamil Nadu",
  "34": "Puducherry", "36": "Telangana", "37": "Andhra Pradesh", "38": "Ladakh"
};

export function useGSTINValidator() {
  const [gstin, setGstin] = useState("");
  const [status, setStatus] = useState<ValidationStatus>("idle");
  const [details, setDetails] = useState<GSTINDetails | null>(null);
  const [errorMsg, setErrorMsg] = useState("");

  const validate = useCallback((input?: string) => {
    const value = (input ?? gstin).toUpperCase().trim();
    if (!value) { setStatus("idle"); setDetails(null); return; }

    if (!GSTIN_REGEX.test(value)) {
      setStatus("invalid");
      setErrorMsg("Invalid GSTIN format. Must be 15 characters (e.g., 27AABCU9603R1ZM)");
      setDetails(null);
      return;
    }

    setStatus("validating");
    setErrorMsg("");

    setTimeout(() => {
      const found = MOCK_GSTIN_DB[value];
      const stateCode = value.slice(0, 2);
      const state = STATE_NAMES[stateCode] || `State (${stateCode})`;
      const pan = value.slice(2, 12);

      const entityDetails: GSTINDetails = found || {
        gstin: value,
        tradeName: `Taxpayer (${pan})`,
        legalName: `Registered Entity (${pan})`,
        address: `${state}, India`,
        city: state,
        state,
        stateCode,
        pincode: "—",
        status: "Active",
        registrationType: "Regular",
        registrationDate: "Verified",
        lastFiledReturn: "GSTR-3B (Current)",
        isValid: true,
      };

      setDetails(entityDetails);
      setStatus(entityDetails.isValid ? "valid" : "invalid");
      if (!entityDetails.isValid) setErrorMsg(`GSTIN is ${entityDetails.status} on GST portal`);
    }, 400);
  }, [gstin]);

  const reset = useCallback(() => {
    setGstin("");
    setStatus("idle");
    setDetails(null);
    setErrorMsg("");
  }, []);

  return { gstin, setGstin, status, details, errorMsg, validate, reset };
}

// ========================
// COMPONENT: Inline GSTIN Field
// ========================

interface GSTINValidatorProps {
  value?: string;
  onChange?: (gstin: string) => void;
  onValidated?: (details: GSTINDetails | null) => void;
  compact?: boolean;
  label?: string;
  className?: string;
}

export function GSTINValidator({ value, onChange, onValidated, compact = false, label, className = "" }: GSTINValidatorProps) {
  const { gstin, setGstin, status, details, errorMsg, validate } = useGSTINValidator();

  const currentValue = value ?? gstin;

  const handleChange = (v: string) => {
    const upper = v.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 15);
    if (onChange) onChange(upper);
    else setGstin(upper);
  };

  const handleValidate = () => {
    validate(currentValue);
  };

  // Notify parent when validation completes
  const prevDetailsRef = useState<GSTINDetails | null>(null);
  if (details !== prevDetailsRef[0] && onValidated) {
    prevDetailsRef[1](details);
    onValidated(details);
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {label && <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</Label>}

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input
            value={currentValue}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="e.g. 27AABCU9603R1ZM"
            maxLength={15}
            className={`font-mono text-sm pr-8 ${
              status === "valid" ? "border-green-500 focus-visible:ring-green-500" :
              status === "invalid" || status === "error" ? "border-destructive focus-visible:ring-destructive" : ""
            }`}
            onKeyDown={(e) => e.key === "Enter" && handleValidate()}
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            {status === "validating" && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            {status === "valid" && <CheckCircle2 className="h-4 w-4 text-green-500" />}
            {status === "invalid" && <XCircle className="h-4 w-4 text-destructive" />}
            {status === "error" && <AlertTriangle className="h-4 w-4 text-yellow-500" />}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={handleValidate} disabled={currentValue.length !== 15 || status === "validating"} className="h-10 px-3">
          {status === "validating" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
        </Button>
      </div>

      {/* Error message */}
      {(status === "invalid" || status === "error") && errorMsg && (
        <p className="text-xs text-destructive flex items-center gap-1">
          <XCircle className="h-3 w-3" /> {errorMsg}
        </p>
      )}

      {/* Valid result — auto-filled details */}
      {status === "valid" && details && (
        <div className={`rounded-lg border bg-card ${compact ? "p-2" : "p-3"} space-y-2`}>
          <div className="flex items-center gap-2">
            <Badge variant="default" className="bg-green-600 text-white text-xs gap-1">
              <CheckCircle2 className="h-3 w-3" /> Verified
            </Badge>
            <Badge variant="outline" className="text-xs">{details.status}</Badge>
            <Badge variant="outline" className="text-xs">{details.registrationType}</Badge>
          </div>

          <div className={`grid ${compact ? "grid-cols-1 gap-1" : "grid-cols-2 gap-2"}`}>
            <div className="flex items-start gap-2">
              <Building2 className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Business Name</p>
                <p className={`font-medium text-foreground ${compact ? "text-xs" : "text-sm"}`}>{details.tradeName}</p>
                {!compact && <p className="text-xs text-muted-foreground">{details.legalName}</p>}
              </div>
            </div>

            <div className="flex items-start gap-2">
              <MapPin className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Registered Address</p>
                <p className={`text-foreground ${compact ? "text-xs" : "text-sm"}`}>{details.address}, {details.city}</p>
                {!compact && <p className="text-xs text-muted-foreground">{details.state} — {details.pincode}</p>}
              </div>
            </div>

            {!compact && (
              <>
                <div className="flex items-start gap-2">
                  <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">State Code</p>
                    <p className="text-sm font-mono text-foreground">{details.stateCode} — {details.state}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">Last Filed Return</p>
                    <p className="text-sm text-foreground">{details.lastFiledReturn}</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Invalid GSTIN with details (e.g. Inactive) */}
      {status === "invalid" && details && (
        <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-3 space-y-2">
          <div className="flex items-center gap-2">
            <Badge variant="destructive" className="text-xs gap-1">
              <XCircle className="h-3 w-3" /> {details.status}
            </Badge>
          </div>
          <div className="flex items-start gap-2">
            <Building2 className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground">{details.tradeName}</p>
              <p className="text-xs text-muted-foreground">{details.address}, {details.city}, {details.state}</p>
            </div>
          </div>
          <p className="text-xs text-destructive flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" /> This GSTIN is {details.status}. ITC claims may not be allowed.
          </p>
        </div>
      )}
    </div>
  );
}

// ========================
// COMPONENT: Standalone Validator Card
// ========================

export function GSTINValidatorCard() {
  const { gstin, setGstin, status, details, errorMsg, validate, reset } = useGSTINValidator();

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input
            value={gstin}
            onChange={(e) => setGstin(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 15))}
            placeholder="Enter GSTIN to verify (e.g. 27AABCU9603R1ZM)"
            maxLength={15}
            className={`font-mono pr-10 ${
              status === "valid" ? "border-green-500 focus-visible:ring-green-500" :
              status === "invalid" || status === "error" ? "border-destructive focus-visible:ring-destructive" : ""
            }`}
            onKeyDown={(e) => e.key === "Enter" && validate()}
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {status === "validating" && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            {status === "valid" && <CheckCircle2 className="h-4 w-4 text-green-500" />}
            {status === "invalid" && <XCircle className="h-4 w-4 text-destructive" />}
            {status === "error" && <AlertTriangle className="h-4 w-4 text-yellow-500" />}
          </div>
        </div>
        <Button onClick={() => validate()} disabled={gstin.length !== 15 || status === "validating"}>
          {status === "validating" ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Verifying…</> : <><Search className="h-4 w-4 mr-1" /> Verify GSTIN</>}
        </Button>
        {status !== "idle" && (
          <Button variant="outline" onClick={reset}>Clear</Button>
        )}
      </div>

      {(status === "invalid" || status === "error") && errorMsg && (
        <div className="p-3 rounded-lg border border-destructive/20 bg-destructive/5">
          <p className="text-sm text-destructive flex items-center gap-2">
            <XCircle className="h-4 w-4 shrink-0" /> {errorMsg}
          </p>
        </div>
      )}

      {status === "valid" && details && (
        <div className="rounded-lg border border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-950/30 p-4 space-y-4">
          <div className="flex items-center gap-2">
            <Badge variant="default" className="bg-green-600 text-white gap-1">
              <CheckCircle2 className="h-3.5 w-3.5" /> GSTIN Verified
            </Badge>
            <Badge variant="outline">{details.status}</Badge>
            <Badge variant="outline">{details.registrationType}</Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Trade Name</p>
              <p className="text-base font-bold text-foreground">{details.tradeName}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Legal Name</p>
              <p className="text-sm font-medium text-foreground">{details.legalName}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Registered Address</p>
              <p className="text-sm text-foreground">{details.address}, {details.city}, {details.state} — {details.pincode}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">State Code</p>
              <p className="text-sm font-mono text-foreground">{details.stateCode} — {details.state}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Registration Date</p>
              <p className="text-sm text-foreground">{details.registrationDate}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Last Filed Return</p>
              <p className="text-sm text-foreground">{details.lastFiledReturn}</p>
            </div>
          </div>
        </div>
      )}

      {status === "invalid" && details && (
        <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Badge variant="destructive" className="gap-1"><XCircle className="h-3.5 w-3.5" /> {details.status}</Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><p className="text-xs text-muted-foreground">Trade Name</p><p className="text-sm font-medium text-foreground">{details.tradeName}</p></div>
            <div><p className="text-xs text-muted-foreground">Address</p><p className="text-sm text-foreground">{details.address}, {details.city}, {details.state}</p></div>
          </div>
          <div className="p-2 rounded bg-destructive/10 text-xs text-destructive flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            This GSTIN is {details.status}. Input Tax Credit claims against this party may be disallowed.
          </div>
        </div>
      )}
    </div>
  );
}
